import React from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Building2, Users2, MapPin } from "lucide-react";
import { toast } from "sonner";
import HealthScoreChip from "@/components/HealthScoreChip";

const COLUMNS = [
  { status: "prospect",  label: "Prospect",  bg: "bg-amber-500",  soft: "bg-amber-50/60 border-amber-200" },
  { status: "active",    label: "Active",    bg: "bg-emerald-600", soft: "bg-emerald-50/60 border-emerald-200" },
  { status: "on_hold",   label: "On hold",   bg: "bg-orange-500",  soft: "bg-orange-50/60 border-orange-200" },
  { status: "inactive",  label: "Inactive",  bg: "bg-slate-500",   soft: "bg-slate-50 border-slate-200" },
];

const TYPE_TONE = {
  SNF: "bg-indigo-100 text-indigo-800 border-indigo-200",
  ALF: "bg-violet-100 text-violet-800 border-violet-200",
  IL:  "bg-sky-100 text-sky-800 border-sky-200",
  Other: "bg-slate-100 text-slate-700 border-slate-200",
};

export default function FacilitiesBoard({ rows, reload, groupBy = "status", reps = [], health = {} }) {
  const grouped = React.useMemo(() => {
    if (groupBy === "rep") {
      const map = {};
      const activeReps = reps.filter((r) => r.active !== false);
      activeReps.forEach((r) => { map[r.user_id] = []; });
      map["__unassigned__"] = [];
      rows.forEach((r) => {
        const key = r.assigned_rep_id || "__unassigned__";
        (map[key] || (map[key] = [])).push(r);
      });
      return map;
    }
    const map = { prospect: [], active: [], on_hold: [], inactive: [] };
    rows.forEach((r) => { (map[r.referral_status] || map.prospect).push(r); });
    return map;
  }, [rows, groupBy, reps]);

  const columns = React.useMemo(() => {
    if (groupBy === "rep") {
      const palette = [
        { bg: "bg-indigo-600", soft: "bg-indigo-50/60 border-indigo-200" },
        { bg: "bg-emerald-600", soft: "bg-emerald-50/60 border-emerald-200" },
        { bg: "bg-orange-500", soft: "bg-orange-50/60 border-orange-200" },
        { bg: "bg-sky-600", soft: "bg-sky-50/60 border-sky-200" },
        { bg: "bg-violet-600", soft: "bg-violet-50/60 border-violet-200" },
        { bg: "bg-rose-600", soft: "bg-rose-50/60 border-rose-200" },
      ];
      const cols = reps.filter((r) => r.active !== false).map((r, i) => ({
        key: r.user_id, label: r.name, ...palette[i % palette.length],
      }));
      cols.push({ key: "__unassigned__", label: "Unassigned", bg: "bg-slate-500", soft: "bg-slate-50 border-slate-200" });
      return cols;
    }
    return COLUMNS.map((c) => ({ key: c.status, label: c.label, bg: c.bg, soft: c.soft }));
  }, [groupBy, reps]);

  const onDragStart = (e, id) => { e.dataTransfer.setData("facility_id", id); };
  const onDrop = async (e, colKey) => {
    e.preventDefault();
    const id = e.dataTransfer.getData("facility_id");
    if (!id) return;
    const current = rows.find((r) => r.facility_id === id);
    if (!current) return;
    try {
      if (groupBy === "rep") {
        const new_rep_id = colKey === "__unassigned__" ? null : colKey;
        if ((current.assigned_rep_id || null) === new_rep_id) return;
        await api.put(`/facilities/${id}`, {
          name: current.name, type: current.type, address: current.address, city: current.city,
          county: current.county, zip_code: current.zip_code, latitude: current.latitude, longitude: current.longitude,
          capacity: current.capacity, census: current.census, payer_mix: current.payer_mix, medicaid_id: current.medicaid_id,
          admin_contact_name: current.admin_contact_name, admin_contact_phone: current.admin_contact_phone,
          admin_contact_email: current.admin_contact_email, corporate_owner_id: current.corporate_owner_id,
          assigned_rep_id: new_rep_id,
          referral_status: current.referral_status, notes: current.notes,
        });
        const targetRep = reps.find((r) => r.user_id === new_rep_id);
        toast.success(new_rep_id ? `Assigned to ${targetRep?.name || "rep"}` : "Unassigned");
      } else {
        if (current.referral_status === colKey) return;
        await api.put(`/facilities/${id}`, {
          name: current.name, type: current.type, address: current.address, city: current.city,
          county: current.county, zip_code: current.zip_code, latitude: current.latitude, longitude: current.longitude,
          capacity: current.capacity, census: current.census, payer_mix: current.payer_mix, medicaid_id: current.medicaid_id,
          admin_contact_name: current.admin_contact_name, admin_contact_phone: current.admin_contact_phone,
          admin_contact_email: current.admin_contact_email, corporate_owner_id: current.corporate_owner_id,
          assigned_rep_id: current.assigned_rep_id,
          referral_status: colKey, notes: current.notes,
        });
        toast.success(`Moved to ${colKey.replace("_", " ")}`);
      }
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Move failed"); }
  };

  return (
    <div className={`grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 ${groupBy === "rep" ? "xl:grid-cols-3 2xl:grid-cols-4" : ""}`} data-testid="facilities-board">
      {columns.map((col) => {
        const items = grouped[col.key] || [];
        return (
          <div
            key={col.key}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => onDrop(e, col.key)}
            className={`rounded-lg border ${col.soft} p-3 min-h-[420px]`}
            data-testid={`board-col-${col.key}`}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className={`w-2.5 h-2.5 rounded-full ${col.bg}`} />
                <div className="font-heading font-bold text-slate-900">{col.label}</div>
              </div>
              <div className="text-xs font-mono-plex text-slate-500">{items.length}</div>
            </div>
            <div className="space-y-2">
              {items.length === 0 && <div className="text-xs text-slate-400 italic p-4 text-center border border-dashed border-slate-200 rounded-md">Drop here</div>}
              {items.map((f) => (
                <Card
                  key={f.facility_id}
                  draggable
                  onDragStart={(e) => onDragStart(e, f.facility_id)}
                  className="p-3 border-slate-200 shadow-none bg-white cursor-grab active:cursor-grabbing transition-transform duration-200 hover:-translate-y-[1px]"
                  data-testid={`board-card-${f.facility_id}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <Link to={`/facilities/${f.facility_id}`} className="text-sm font-medium text-slate-900 hover:underline min-w-0 truncate">{f.name}</Link>
                    <Badge variant="outline" className={`text-[10px] shrink-0 ${TYPE_TONE[f.type] || TYPE_TONE.Other}`}>{f.type}</Badge>
                  </div>
                  <div className="mt-1.5 flex items-center justify-between gap-1.5 text-[11px] text-slate-500">
                    <span className="flex items-center gap-1.5 min-w-0"><MapPin className="w-3 h-3 shrink-0" /><span className="truncate">{f.city}, {f.county}</span></span>
                    <HealthScoreChip score={health[f.facility_id]} />
                  </div>
                  <div className="mt-2 flex items-center justify-between text-[11px] font-mono-plex text-slate-500">
                    <span className="flex items-center gap-1"><Users2 className="w-3 h-3" /> {f.census ?? "—"}/{f.capacity ?? "—"}</span>
                    {f.corporate_owner_name && <span className="truncate max-w-[110px] flex items-center gap-1"><Building2 className="w-3 h-3" /> {f.corporate_owner_name}</span>}
                  </div>
                  {f.assigned_rep_name && (
                    <div className="mt-2 pt-2 border-t border-slate-100 flex items-center gap-1.5">
                      <div className="w-5 h-5 rounded-full bg-slate-900 text-white text-[9px] flex items-center justify-center font-medium">
                        {f.assigned_rep_name.split(" ").map(s => s[0]).slice(0, 2).join("").toUpperCase()}
                      </div>
                      <div className="text-[11px] text-slate-600 truncate">{f.assigned_rep_name}</div>
                    </div>
                  )}
                </Card>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
